import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { __ } from 'react-i18n';
import {
  FlexCol,
  FlexRow,
  FlexRowCenterVertical,
} from '../../Defaults/Flexbox/Flexbox';
import { Title } from '../General/Title';
import InfoRow from './InfoRow';
import { StockInfo } from '../Product/StockInfo';
import { BigButtonWithRequest } from '../Product/BigButtonWithRequest';
import { PriceInfo } from './PriceInfo';
import { CountSetter } from './CountSetter';
import { MinAmountInfoText } from '../Cart/Items/MinAmountInfoText';
import { WholePackagesOnly } from '../Cart/Items/WholePackageOnlyInfoText';
import {
  prop,
  round,
  formatPriceToString,
  formatPrice,
} from '../../../utilities';
import { getPackagesString } from '../selectors';

interface Props {
  inPackage: number | null;
  price: number | string | null;
  currency: string;
  unit: string;
  onStock: number;
  addToRequest: any;
  count: number;
  addToCart: any;
  handleCountChange: (newValue) => void;
  user: any;
  mocPrice?: string;
  sale?: string;
  b2bDiscount?: string;
  unpackingCost: number;
  storesInfo: any;
  statusShowAddToCart: boolean;
  isSpojMatOrPreDrev: boolean;
  showPrice: boolean;
  priceForUnpacking: number;
  customerPrices: any[];
  isInCart?: boolean;
  onStockExternal?: number;
  isMobile?: boolean;
  oldPriceCorrect: any;
  wholePackagesOnly: boolean;
  defaultPackageSize: number;
  no_handling_fee: boolean;
  qtyDiscounts: any[];
  boxQuantity: number;
  isVykurovaciaOrPletiva: boolean;
}

function RightUpper({
  inPackage,
  price,
  currency,
  unit,
  onStock,
  addToRequest,
  addToCart,
  handleCountChange,
  count,
  user,
  mocPrice,
  sale,
  b2bDiscount,
  unpackingCost,
  storesInfo,
  statusShowAddToCart,
  isSpojMatOrPreDrev,
  showPrice,
  priceForUnpacking,
  customerPrices,
  isInCart,
  onStockExternal = 0,
  isMobile = false,
  oldPriceCorrect,
  defaultPackageSize,
  wholePackagesOnly,
  no_handling_fee,
  qtyDiscounts,
  boxQuantity,
  isVykurovaciaOrPletiva,
}: Props) {
  const correctOnstock = onStock === 0 ? onStockExternal : onStock;

  const isB2B = user && user.b2b;
  const pricesText = isB2B ? __('Cena bez DPH') : __('Cena s DPH');
  const totalPriceText = isB2B ? __('Spolu bez DPH') : __('Spolu s DPH');
  const isMks = unit === 'Mks';

  // Ak mame kd sipozicii defaultnu hodnotu vlekosti balenia, pouzijeme ju
  const step = defaultPackageSize
    ? defaultPackageSize
    : inPackage
    ? inPackage
    : unit === 'Mks'
    ? 0.001
    : 1;
  const correctStep = wholePackagesOnly
    ? defaultPackageSize
      ? defaultPackageSize
      : step
    : step;

  const trueCount = unit === 'Mks' ? count * 1000 : count;
  const correctPriceForUnpacking = priceForUnpacking
    ? priceForUnpacking * trueCount
    : 5;

  const showMinPackageInfo = isSpojMatOrPreDrev
    ? correctPriceForUnpacking < 10 &&
      inPackage &&
      inPackage >= 0.026 &&
      inPackage > count &&
      +count !== correctOnstock
    : false;

  let totalPrice = price ? +price * count : '';
  const correctCustomerPrice = customerPrices
    ? customerPrices.find(cp => cp.quantity >= count)
    : null;
  if (correctCustomerPrice) {
    totalPrice = isMks
      ? prop(correctCustomerPrice, 'unitprice') * 1000 * count
      : prop(correctCustomerPrice, 'unitprice') * count;
  }

  const correctUnpackingCost = unpackingCost * trueCount;
  let pricePerUnit = price
    ? b2bDiscount
      ? round(+price + +b2bDiscount, 2).toString()
      : round(+price, 2).toString()
    : '';

  const oldPrice = isB2B
    ? b2bDiscount
      ? +totalPrice + +b2bDiscount * trueCount
      : +totalPrice
    : null;

  const oldPriceWithUnpacking = oldPrice
    ? showMinPackageInfo
      ? round(correctUnpackingCost + +oldPrice, 2).toString()
      : round(+oldPrice, 2).toString()
    : null;
  const priceWithUnpacking = showMinPackageInfo
    ? correctUnpackingCost + +totalPrice
    : +totalPrice;
  let minimalPrice: any = isSpojMatOrPreDrev
    ? showPrice && totalPrice
      ? isB2B
        ? +priceWithUnpacking > 0.75
          ? priceWithUnpacking
          : '0.75'
        : +priceWithUnpacking > 0.75
        ? priceWithUnpacking
        : '0.92'
      : ''
    : totalPrice;

  const activeQtyDiscount = qtyDiscounts.find((discount, i) => {
    return qtyDiscounts[i + 1]
      ? discount.minimal_amount <= count &&
          qtyDiscounts[i + 1].minimal_amount > count
      : discount.minimal_amount <= count;
  });
  if (activeQtyDiscount) {
    minimalPrice =
      minimalPrice - (activeQtyDiscount.sale_percentage / 100) * minimalPrice;
  }

  const correctPerUnitPrice = round(minimalPrice / +count, 2);
  const pricePerOne = isMks ? +correctPerUnitPrice / 1000 : '';
  const pricePerOneNumber =
    +pricePerOne > 0 ? (+pricePerOne < 0.01 ? 0.01 : pricePerOne) : '';
  const pricePerOneFinal = pricePerOneNumber
    ? formatPriceToString(round(pricePerOneNumber, 2))
    : '';

  // Nezobrazujem cenu ak jednoducho vysledna cena je 0 a sme na na spoj material
  // Nezobrazujem cenu ak nemam dostupnost som na spoj
  if (
    (isSpojMatOrPreDrev && !price) ||
    (isSpojMatOrPreDrev && correctOnstock === 0)
  ) {
    minimalPrice = null;
    pricePerUnit = '';
  }

  const hasUnrestrictedOrders = user
    ? user.unrestricted_order === 0
      ? false
      : true
    : false;

  const noStock = statusShowAddToCart
    ? hasUnrestrictedOrders
      ? false
      : +correctOnstock === 0
    : true;

  const oldPriceCorrectFinall = oldPriceCorrect === 0 ? '' : oldPriceCorrect;

  const correctPackageSize = defaultPackageSize
    ? defaultPackageSize
    : inPackage;

  const rightUpperContent = () => {
    return (
      <>
        {correctPackageSize ? (
          <InfoRow
            noPaddingTop={true}
            title={__('Balenie')}
            value={getPackagesString(correctPackageSize, unit)}
            hasBorderBottom={boxQuantity > 0 ? false : true}
            noPaddingBottom={boxQuantity > 0 ? true : false}
          />
        ) : null}
        {boxQuantity ? (
          <InfoRow
            noPaddingTop={true}
            title={
              isVykurovaciaOrPletiva
                ? __('Množstvo v balení')
                : __('Množstvo v kartóne')
            }
            value={getPackagesString(boxQuantity, unit)}
            hasBorderTop={false}
            hasBorderBottom={true}
          />
        ) : null}
        {mocPrice ? (
          <InfoRow
            noPaddingTop={!correctPackageSize ? true : false}
            title={__('Doporučená MOC cena s DPH')}
            value={`${formatPriceToString(mocPrice, currency)} / ${unit}`}
          />
        ) : null}
        {sale ? <InfoRow title={__('Zľava')} value={`${sale}%`} /> : null}
        {pricePerUnit ? (
          <InfoRow
            title={pricesText}
            value={`${formatPriceToString(
              correctPerUnitPrice,
              currency,
            )} / ${unit}`}
            hasBorderTop={false}
          />
        ) : null}
        {pricePerOneFinal ? (
          <InfoRow
            title={__('Orientačná cena za 1 ks')}
            value={`${pricePerOneFinal}`}
            hasBorderTop={false}
          />
        ) : null}
        {!!qtyDiscounts && qtyDiscounts.length > 0 && !!price && (
          <QtyDiscountWrapper>
            <QtyDiscountTitle>{__('Množstevné zľavy')}</QtyDiscountTitle>
            <QtyDiscountItemsWrapper>
              {qtyDiscounts.map((discount, i) => {
                return (
                  <QtyDiscountItem
                    key={`${discount.minimal_amount}`}
                    isActive={
                      qtyDiscounts[i + 1]
                        ? discount.minimal_amount <= count &&
                          qtyDiscounts[i + 1].minimal_amount > count
                        : discount.minimal_amount <= count
                    }
                    // tslint:disable-next-line:jsx-no-lambda
                    onClick={() => handleCountChange(discount.minimal_amount)}
                  >
                    <QtyDiscountItemCount>
                      {`${__('min.')} `}
                      <QtyDiscountItemMinimalCount>
                        {`${discount.minimal_amount} ${unit} `}
                      </QtyDiscountItemMinimalCount>
                      {`(-${discount.sale_percentage}%):`}
                    </QtyDiscountItemCount>
                    <QtyDiscountItemPercentage>
                      <QtyDiscountItemPrice>{`${round(
                        +price - (discount.sale_percentage / 100) * +price,
                        2,
                      )} ${currency}`}</QtyDiscountItemPrice>
                      {` / ${unit}`}
                    </QtyDiscountItemPercentage>
                    {/* <QtyDiscountItemCount>
                      {`${__('zľava')} ${discount.sale_percentage}%`}
                    </QtyDiscountItemCount> */}
                  </QtyDiscountItem>
                );
              })}
            </QtyDiscountItemsWrapper>
          </QtyDiscountWrapper>
        )}
        <CountStockWrapper>
          <CountSetter
            step={correctStep}
            unit={unit}
            count={count}
            onChange={handleCountChange}
            onStock={correctOnstock}
            isB2B={user && user.b2b}
            wholePackagesOnly={wholePackagesOnly}
            defaultPackageSize={correctPackageSize}
            noHandlingFee={no_handling_fee}
          />
          <StockInfo
            lessInfo={isMobile}
            withoutExactStockCount={false}
            justify={'flex-end'}
            onStock={onStock}
            onStockExternal={onStockExternal}
            unit={unit}
            bottomMargin={false}
            storesInfo={storesInfo}
            moveLeft={true}
          />
        </CountStockWrapper>
        {minimalPrice ? (
          <PriceInfo
            price={round(minimalPrice, 2).toString()}
            currency={currency}
            text={totalPriceText}
            oldPrice={oldPriceCorrectFinall}
          />
        ) : null}
        {wholePackagesOnly ? <WholePackagesOnly /> : null}
        {showMinPackageInfo ? (
          !wholePackagesOnly ? (
            <MinimumPackageWrapper>
              <MinAmountInfoText borderBottom={0} />
            </MinimumPackageWrapper>
          ) : null
        ) : null}
        <BigButtonWithRequest
          onRequestClick={addToRequest}
          onButtonClick={addToCart}
          noStock={noStock}
          isInCart={isInCart}
        />
      </>
    );
  };
  return (
    <>
      <DesktopWrapper>{rightUpperContent()}</DesktopWrapper>
      <MobileWrapper>{rightUpperContent()}</MobileWrapper>
    </>
  );
}

const Wrapper = styled(FlexCol)`
  width: 100%;
  padding: ${rem(24)};
  max-width: ${rem(448)};
  box-shadow: ${({ theme }) => theme.boxShadow.default};
  border: ${({ theme }) => theme.borders.secondary};
  border-radius: ${rem(4)};

  ${({ theme }) => theme.mediab.l925`
    padding: 0;
    border: 0;
    box-shadow: none;
  `}
`;

const MinimumPackageWrapper = styled.div`
  margin-bottom: ${rem(24)};
`;

const DesktopWrapper = styled(Wrapper)`
  ${({ theme }) => theme.mediab.l925`
   display: none;
  `}
`;

const MobileWrapper = styled(Wrapper)`
  display: none;

  ${({ theme }) => theme.mediab.l925`
   display: flex;
   margin-bottom: ${rem(26)};
  `}
`;

const CountStockWrapper = styled(FlexRowCenterVertical)`
  justify-content: space-between;
  margin-top: ${rem(24)};
  width: 100%;
  margin-bottom: ${rem(16)};
`;

const QtyDiscountWrapper = styled.div`
  margin-top: ${rem(24)};
`;

const QtyDiscountTitle = styled.h3`
  margin: 0;
  margin-bottom: ${rem(16)};
  color: ${({ theme }) => theme.colors.primary};
  font-family: ${({ theme }) => theme.font.primary};
  font-size: ${rem(20)};
  font-weight: 500;
`;

const QtyDiscountItemsWrapper = styled(FlexRowCenterVertical)`
  flex-flow: row wrap;
  gap: ${rem(4)};
`;

const QtyDiscountItem = styled.button<{ isActive?: boolean }>`
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  border: ${({ theme }) => theme.borders.secondary};
  border-color: ${({ theme, isActive }) =>
    isActive ? theme.colors.primary : '#cccccc'};
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: ${rem(13)};
  background: white;
  box-shadow: ${({ theme }) => theme.boxShadow.input};
  cursor: pointer;

  &:hover {
    /* opacity: 0.8; */
    transition: border-color 0.2s ease-in;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const QtyDiscountItemCount = styled.div`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: ${({ theme }) => theme.font.primary};
  font-size: ${rem(14)};
  font-weight: 400;
  margin-bottom: ${rem(8)};
`;

const QtyDiscountItemMinimalCount = styled.strong`
  color: ${({ theme }) => theme.colors.primary};
`;

const QtyDiscountItemPercentage = styled.div`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: ${({ theme }) => theme.font.primary};
  font-size: ${rem(16)};
  /* margin-bottom: ${rem(8)}; */
`;

const QtyDiscountItemPrice = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-family: ${({ theme }) => theme.font.primary};
  font-size: ${rem(16)};
  font-weight: 700;
`;

export { RightUpper };
